import { StatsData, CategoryStats, BreakdownItem } from './types';

export const TEMPLATE_CATEGORIES = {
  ORDER: {
    label: 'Order Templates',
    types: [
      'order_confirmation',
      'order_ready',
      'order_cancelled',
      'order_delivered',
    ],
    icon: 'ShoppingCartOutlined',
    color: '#1890ff',
    helpText: 'Order-related emails',
  },
  USER: {
    label: 'User Templates',
    types: [
      'user_registration',
      'user_welcome',
      'password_reset',
      'email_verification',
    ],
    icon: 'UserOutlined',
    color: '#722ed1',
    helpText: 'User account emails',
  },
  MERCHANT: {
    label: 'Merchant Templates',
    types: [
      'merchant_registration',
      'merchant_approval',
      'merchant_rejection',
    ],
    icon: 'SettingOutlined',
    color: '#fa8c16',
    helpText: 'Merchant management',
  },
  LOYALTY: {
    label: 'Loyalty Templates',
    types: ['loyalty_points_earned', 'loyalty_points_redeemed'],
    icon: 'GiftOutlined',
    color: '#eb2f96',
    helpText: 'Loyalty program emails',
  },
};

export const STATUS_STATS = [
  {
    key: 'total',
    label: 'Total Templates',
    color: '#8c8c8c',
    icon: 'MailOutlined',
    helpText: 'All templates',
  },
  {
    key: 'active',
    label: 'Active Templates',
    color: '#52c41a',
    icon: 'CheckCircleOutlined',
    helpText: 'Ready to use',
  },
  {
    key: 'draft',
    label: 'Draft Templates',
    color: '#faad14',
    icon: 'ClockCircleOutlined',
    helpText: 'In progress',
  },
  {
    key: 'inactive',
    label: 'Inactive Templates',
    color: '#ff4d4f',
    icon: 'CloseCircleOutlined',
    helpText: 'Disabled',
  },
];

export const STAT_CARDS_LOADING_COUNT = 8;
export const BREAKDOWN_TITLE = 'Template Type Breakdown';
export const NO_BREAKDOWN_TEXT = 'No templates configured for specific types';

/** Get status stat value (total/active/draft/inactive) as number */
export const getStatusStatValue = (
  statsData: StatsData,
  key: string
): number => {
  const v = statsData[key as keyof StatsData];
  return typeof v === 'number' ? v : 0;
};

/** Extract stats data from API response or direct stats object */
export const extractStatsData = (
  stats: StatsData | { data?: StatsData } | null
): StatsData => {
  if (!stats) return {};
  if ('data' in stats && stats.data) return stats.data;
  return stats as StatsData;
};

/** Calculate category totals from byType data */
export const calculateCategoryStats = (
  byTypeData: { [key: string]: number } | undefined
): CategoryStats => {
  if (!byTypeData) {
    return {
      orderTemplates: 0,
      userTemplates: 0,
      merchantTemplates: 0,
      loyaltyTemplates: 0,
    };
  }
  return {
    orderTemplates: TEMPLATE_CATEGORIES.ORDER.types.reduce(
      (sum, type) => sum + (byTypeData[type] || 0),
      0
    ),
    userTemplates: TEMPLATE_CATEGORIES.USER.types.reduce(
      (sum, type) => sum + (byTypeData[type] || 0),
      0
    ),
    merchantTemplates: TEMPLATE_CATEGORIES.MERCHANT.types.reduce(
      (sum, type) => sum + (byTypeData[type] || 0),
      0
    ),
    loyaltyTemplates: TEMPLATE_CATEGORIES.LOYALTY.types.reduce(
      (sum, type) => sum + (byTypeData[type] || 0),
      0
    ),
  };
};

/** Format template type label from snake_case to Title Case */
export const formatTypeLabel = (type: string): string =>
  type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/** Convert byType data to breakdown items */
export const getBreakdownItems = (
  byTypeData: { [key: string]: number } | undefined
): BreakdownItem[] => {
  if (!byTypeData || Object.keys(byTypeData).length === 0) return [];
  return Object.entries(byTypeData)
    .map(([type, count]) => ({
      type,
      count,
      label: formatTypeLabel(type),
    }))
    .sort((a, b) => b.count - a.count);
};
